import React, { useCallback, useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { getRegionCodes } from '@luxuryescapes/lib-regions';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import DebugModal from '~/components/DebugModal/DebugModal';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  Voucher,
  createVoucherDisplayConfig,
  expireVoucherDisplayConfig,
  getVoucherDisplayConfig,
  listVouchers,
  updateVoucherDisplayConfig,
} from '~/services/PromoService';

import SortableVoucherItem, { VoucherOption } from './SortableVoucherItem';

// TODO: The contract uses snake_case (PromoDisplayConfig) but this code uses camelCase.
// Using Record<string, any> until the component is migrated to match the contract schema.
type VoucherDisplayConfig = Record<string, any>;
type AuthState = 'logged_in' | 'logged_out' | 'logged_in_and_out';
type ExpiryType = 'days' | 'date';
type DisplayType = 'carousel' | 'snackbar';
type DisplayMode = 'appear_once' | 'persistence';

interface FormValues {
  urlPaths: Array<{ value: string }>;
  source: string;
  medium: string;
  campaign: string;
  headerText: string;
  bodyText: string;
  startDate: string;
  endDate: string;
  voucherExpiryDays: number | '';
  voucherExpiryDate: string;
  delayDuration: number | '';
}

function formatDateTimeLocal(dateStr?: string): string {
  if (!dateStr) {
    return '';
  }
  return dateStr.slice(0, 16);
}

function normalizeBrand(brand: string): string {
  if (brand === 'newwhitelabel' || brand === 'yidu') {
    return 'luxuryescapes';
  }
  return brand;
}

function getDefaultValues(config?: VoucherDisplayConfig): FormValues {
  return {
    urlPaths: config?.urlPaths?.map((p) => ({ value: p })) ?? [{ value: '' }],
    source: config?.source ?? '',
    medium: config?.medium ?? '',
    campaign: config?.campaign ?? '',
    headerText: config?.headerText ?? '',
    bodyText: config?.bodyText ?? '',
    startDate: formatDateTimeLocal(config?.startDate),
    endDate: formatDateTimeLocal(config?.endDate),
    voucherExpiryDays: config?.voucherExpiryDays ?? '',
    voucherExpiryDate: formatDateTimeLocal(config?.voucherExpiryDate),
    delayDuration: config?.delayDuration ?? '',
  };
}

const voucherDisplayConfigQueryKeys = {
  all: ['voucherDisplayConfig'] as const,
  detail: (id: string) => [...voucherDisplayConfigQueryKeys.all, id] as const,
};

export default function VoucherDisplayConfigDetailPage() {
  const { voucherDisplayConfigId } = useParams<{ voucherDisplayConfigId: string }>();
  const isEditing = !!voucherDisplayConfigId;
  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useCurrentTenant();
  const queryClient = useQueryClient();
  const { replace } = useAppNavigation();

  const {
    data: config,
    isLoading,
    error,
  } = useTanstackQuery({
    queryKey: voucherDisplayConfigQueryKeys.detail(voucherDisplayConfigId),
    queryFn: async () => {
      const response = await getVoucherDisplayConfig(voucherDisplayConfigId);
      return response.result;
    },
    enabled: isEditing,
  });

  const [authState, setAuthState] = useState<AuthState>('logged_in_and_out');
  const [region, setRegion] = useState<string>('AU');
  const [expiryType, setExpiryType] = useState<ExpiryType>('days');
  const [displayType, setDisplayType] = useState<DisplayType>('carousel');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('appear_once');
  const [initialized, setInitialized] = useState(!isEditing);
  const [selectedVouchers, setSelectedVouchers] = useState<Array<VoucherOption>>([]);
  const [voucherSearch, setVoucherSearch] = useState('');

  const { data: voucherOptions = [], isLoading: isLoadingVouchers } = useTanstackQuery({
    queryKey: ['vouchers', 'list', voucherSearch],
    queryFn: async () => {
      const response = await listVouchers({
        voucher_name: voucherSearch || undefined,
        limit: 50,
      });
      return response.result.vouchers
        .filter((v: Voucher) => !v.deactivatedAt)
        .map((v: Voucher) => ({
          voucherId: v.voucherId,
          voucherName: v.voucherName,
        }));
    },
  });

  const { data: existingVouchers } = useTanstackQuery({
    queryKey: ['vouchers', 'byIds', config?.vouchers?.map((v) => v.voucherId)],
    queryFn: async () => {
      if (!config?.vouchers?.length) {
        return [];
      }
      const ids = config.vouchers.map((v) => v.voucherId);
      const response = await listVouchers({ voucher_ids: ids });
      const voucherMap = new Map<string, VoucherOption>(
        response.result.vouchers.map((v: Voucher) => [
          v.voucherId,
          { voucherId: v.voucherId, voucherName: v.voucherName },
        ]),
      );
      return ids.map((id) => voucherMap.get(id)).filter((v): v is VoucherOption => Boolean(v));
    },
    enabled: !!config?.vouchers?.length && !initialized,
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (!config || initialized) {
      return;
    }
    const hasVouchers = !!config.vouchers?.length;
    if (hasVouchers && !existingVouchers) {
      return;
    }
    setAuthState(config.authState ?? 'logged_in_and_out');
    setRegion(config.region ?? 'AU');
    setExpiryType(config.voucherExpiryDate ? 'date' : 'days');
    setDisplayType(config.displayType ?? 'carousel');
    setDisplayMode(config.displayMode ?? 'appear_once');
    setSelectedVouchers(existingVouchers ?? []);
    reset(getDefaultValues(config));
    setInitialized(true);
  }, [config, existingVouchers, initialized, reset]);

  const {
    fields: urlPathFields,
    append: appendUrlPath,
    remove: removeUrlPath,
  } = useFieldArray({ control, name: 'urlPaths' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleVoucherDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setSelectedVouchers((prev) => {
      const oldIndex = prev.findIndex((v) => v.voucherId === active.id);
      const newIndex = prev.findIndex((v) => v.voucherId === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const handleRemoveVoucher = useCallback((voucherId: string) => {
    setSelectedVouchers((prev) => prev.filter((v) => v.voucherId !== voucherId));
  }, []);

  const handleCreate = useCallback(
    async (data: FormValues) => {
      try {
        const response = await createVoucherDisplayConfig({
          region: region as any,
          brand: normalizeBrand(tenant.brand) as any,
          urlPaths: data.urlPaths.map((p) => p.value).filter(Boolean),
          voucherIds: selectedVouchers.map((v) => v.voucherId),
          source: data.source,
          medium: data.medium || undefined,
          campaign: data.campaign || undefined,
          headerText: data.headerText || undefined,
          bodyText: data.bodyText || undefined,
          authState,
          displayType,
          displayMode: displayType === 'snackbar' ? displayMode : undefined,
          startDate: data.startDate || undefined,
          endDate: data.endDate || undefined,
          voucherExpiryDays:
            expiryType === 'days' && data.voucherExpiryDays ? Number(data.voucherExpiryDays) : undefined,
          voucherExpiryDate: expiryType === 'date' && data.voucherExpiryDate ? data.voucherExpiryDate : undefined,
          delayDuration: displayType === 'snackbar' && data.delayDuration ? Number(data.delayDuration) : undefined,
        });
        enqueueSnackbar('Voucher Display Config created successfully', { variant: 'success' });
        replace(`/marketing/voucher-display-config/${response.result.voucherDisplayConfigId}`);
      } catch (error) {
        enqueueSnackbar(`Voucher Display Config creation failed: ${JSON.stringify(error)}`, { variant: 'error' });
      }
    },
    [region, tenant.brand, authState, displayType, displayMode, expiryType, selectedVouchers, enqueueSnackbar, replace],
  );

  const handleUpdate = useCallback(
    async (data: FormValues) => {
      if (!config) {
        return;
      }
      try {
        await updateVoucherDisplayConfig({
          voucherDisplayConfigId: config.voucherDisplayConfigId,
          region: region as any,
          brand: normalizeBrand(tenant.brand) as any,
          urlPaths: data.urlPaths.map((p) => p.value).filter(Boolean),
          voucherIds: selectedVouchers.map((v) => v.voucherId),
          source: data.source,
          medium: data.medium || undefined,
          campaign: data.campaign || undefined,
          headerText: data.headerText || undefined,
          bodyText: data.bodyText || undefined,
          authState,
          displayType,
          displayMode: displayType === 'snackbar' ? displayMode : undefined,
          startDate: data.startDate || undefined,
          endDate: data.endDate || undefined,
          voucherExpiryDays:
            expiryType === 'days' && data.voucherExpiryDays ? Number(data.voucherExpiryDays) : undefined,
          voucherExpiryDate: expiryType === 'date' && data.voucherExpiryDate ? data.voucherExpiryDate : undefined,
          delayDuration: displayType === 'snackbar' && data.delayDuration ? Number(data.delayDuration) : undefined,
        });
        queryClient.invalidateQueries({ queryKey: voucherDisplayConfigQueryKeys.detail(voucherDisplayConfigId) });
        enqueueSnackbar('Voucher Display Config updated successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Voucher Display Config update failed: ${JSON.stringify(error)}`, { variant: 'error' });
      }
    },
    [
      config,
      region,
      tenant.brand,
      authState,
      displayType,
      displayMode,
      expiryType,
      selectedVouchers,
      queryClient,
      voucherDisplayConfigId,
      enqueueSnackbar,
    ],
  );

  const handleExpire = useCallback(async () => {
    if (!config) {
      return;
    }
    try {
      await expireVoucherDisplayConfig(config.voucherDisplayConfigId);
      queryClient.invalidateQueries({ queryKey: voucherDisplayConfigQueryKeys.detail(voucherDisplayConfigId) });
      enqueueSnackbar('Voucher Display Config expired successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to expire: ${error}`, { variant: 'error' });
    }
  }, [config, queryClient, voucherDisplayConfigId, enqueueSnackbar]);

  if (isEditing && isLoading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (isEditing && (error || !config)) {
    return (
      <Container maxWidth="xl">
        <Box py={4}>
          <Alert severity="error">{error ? `Failed to load config: ${error.message}` : 'Config not found'}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mt={2} mb={1}>
        <Button variant="text" component={Link} to="/marketing/voucher-display-config">
          &larr; Back to Voucher Display Config
        </Button>
      </Box>
      <PageSubheader title={isEditing ? 'Edit Voucher Display Config' : 'Create Voucher Display Config'} />

      {isEditing && config && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              <b>ID:</b> {config.voucherDisplayConfigId}
            </Typography>
            <Typography variant="body2">
              <b>Status:</b>{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: config.status === 'active' ? 'success.main' : 'error.main',
                  fontWeight: 'bold',
                }}
              >
                {config.status}
              </Typography>
            </Typography>
          </Stack>
          <DebugModal type="generic" data={config} />
        </Box>
      )}

      <Box mt={3} component="form" onSubmit={handleSubmit(isEditing ? handleUpdate : handleCreate)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Autocomplete
              id="region"
              options={getRegionCodes()}
              value={region}
              onChange={(_, newValue) => setRegion(newValue || 'AU')}
              renderInput={(params) => <TextField {...params} label="Region" required />}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Autocomplete
              id="authState"
              options={['logged_in', 'logged_out', 'logged_in_and_out'] as Array<AuthState>}
              value={authState}
              onChange={(_, newValue) => setAuthState(newValue || 'logged_in_and_out')}
              renderInput={(params) => <TextField {...params} label="Auth State" required />}
            />
          </Grid>

          <Grid size={{ xs: 4 }}>
            <Autocomplete
              id="displayType"
              options={['carousel', 'snackbar'] as Array<DisplayType>}
              value={displayType}
              onChange={(_, newValue) => {
                const next = newValue || 'carousel';
                setDisplayType(next);
                if (next === 'snackbar' && selectedVouchers.length > 1) {
                  setSelectedVouchers((prev) => prev.slice(0, 1));
                }
              }}
              renderInput={(params) => <TextField {...params} label="Display Type" required />}
            />
          </Grid>

          {displayType === 'snackbar' ? (
            <>
              <Grid size={{ xs: 4 }}>
                <TextField
                  label="Delay Duration (ms)"
                  variant="outlined"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  {...register('delayDuration')}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Autocomplete
                  id="displayMode"
                  options={['appear_once', 'persistence'] as Array<DisplayMode>}
                  value={displayMode}
                  onChange={(_, newValue) => setDisplayMode(newValue || 'appear_once')}
                  renderInput={(params) => <TextField {...params} label="Display Mode" required />}
                />
              </Grid>
            </>
          ) : (
            <Grid size={{ xs: 8 }} />
          )}

          <Grid size={{ xs: 4 }}>
            <TextField
              label="UTM Source"
              variant="outlined"
              fullWidth
              required
              {...register('source', { required: 'UTM source is required' })}
              error={!!errors.source}
              helperText={errors.source?.message}
              autoComplete="off"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="UTM Medium"
              variant="outlined"
              fullWidth
              {...register('medium')}
              autoComplete="off"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="UTM Campaign"
              variant="outlined"
              fullWidth
              {...register('campaign')}
              autoComplete="off"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Header Text"
              variant="outlined"
              fullWidth
              required
              {...register('headerText', { required: 'Header text is required' })}
              error={!!errors.headerText}
              helperText={errors.headerText?.message}
              autoComplete="off"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Body Text"
              variant="outlined"
              fullWidth
              required
              {...register('bodyText', { required: 'Body text is required' })}
              error={!!errors.bodyText}
              helperText={errors.bodyText?.message}
              multiline
              rows={2}
              autoComplete="off"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              label="Start Date"
              variant="outlined"
              fullWidth
              type="datetime-local"
              slotProps={{ inputLabel: { shrink: true } }}
              {...register('startDate')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="End Date"
              variant="outlined"
              fullWidth
              type="datetime-local"
              slotProps={{ inputLabel: { shrink: true } }}
              {...register('endDate')}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Voucher Expiry
            </Typography>
            <RadioGroup row value={expiryType} onChange={(e) => setExpiryType(e.target.value as ExpiryType)}>
              <FormControlLabel value="days" control={<Radio />} label="Expiry Days" />
              <FormControlLabel value="date" control={<Radio />} label="Expiry Date" />
            </RadioGroup>
          </Grid>
          {expiryType === 'days' && (
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Voucher Expiry Days"
                variant="outlined"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                {...register('voucherExpiryDays')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          )}
          {expiryType === 'date' && (
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Voucher Expiry Date"
                variant="outlined"
                fullWidth
                type="datetime-local"
                slotProps={{ inputLabel: { shrink: true } }}
                {...register('voucherExpiryDate')}
              />
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <Grid size={{ xs: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              URL Paths
            </Typography>
            {urlPathFields.map((field, index) => (
              <Stack key={field.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. /offers/hotel-deal"
                  {...register(`urlPaths.${index}.value` as const)}
                  error={!!errors.urlPaths?.[index]?.value}
                  helperText={errors.urlPaths?.[index]?.value?.message}
                />
                <Button onClick={() => removeUrlPath(index)} disabled={urlPathFields.length === 1} color="error">
                  <DeleteIcon />
                </Button>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => appendUrlPath({ value: '' })}>
              Add URL Path
            </Button>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              Vouchers{displayType === 'snackbar' && ' (max 1 for snackbar)'}
            </Typography>
            <Autocomplete
              multiple
              options={voucherOptions.filter(
                (option) => !selectedVouchers.some((v) => v.voucherId === option.voucherId),
              )}
              value={selectedVouchers}
              loading={isLoadingVouchers}
              getOptionLabel={(option) => `${option.voucherName} (${option.voucherId})`}
              isOptionEqualToValue={(option, value) => option.voucherId === value.voucherId}
              onInputChange={(_, value) => setVoucherSearch(value)}
              onChange={(_, newValue) => {
                if (displayType === 'snackbar') {
                  setSelectedVouchers(newValue.slice(-1));
                } else {
                  setSelectedVouchers(newValue);
                }
              }}
              renderTags={() => null}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Search vouchers by name" />
              )}
            />
            {selectedVouchers.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Drag to reorder. This order will be used when displaying vouchers.
                </Typography>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleVoucherDragEnd}>
                  <SortableContext
                    items={selectedVouchers.map((v) => v.voucherId)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedVouchers.map((voucher, index) => (
                      <SortableVoucherItem
                        key={voucher.voucherId}
                        voucher={voucher}
                        index={index}
                        onRemove={handleRemoveVoucher}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
            )}
          </Grid>
        </Grid>

        <Box mt={3} mb={4}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" type="submit">
              {isEditing ? 'Update' : 'Create'}
            </Button>
            {isEditing && config?.status === 'active' && (
              <Button variant="contained" color="error" onClick={handleExpire}>
                Expire
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Container>
  );
}
